'use client';

import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  CSSProperties,
} from 'react';
import Icon from '@/components/ui/AppIcon';
import UploadZone from '@/components/pdf/UploadZone';
import { TOOL_COLORS } from '@/constants/toolColors';
import { useToast } from '@/components/ui/Toast';
import { isEncryptedPDF } from '@/utils/pdf';
import { FileCard } from '@/components/pdf/FileCard';
import { getPdfMeta } from '@/constants/pdfConfig';

const SIGN_THEME = {
  primary: '#6366F1',
  primaryLight: '#EEF2FF',
  primaryBorder: '#C7D2FE',
  primaryShadow: 'rgba(99, 102, 241, 0.28)',
};

const C = TOOL_COLORS.sign;
const API_BASE = process.env.API_BASE_URL || 'https://api.luvupdf.com';
const MAX_MB = 50 * 1024 * 1024;
const ZOOM_MIN = 0.5,
  ZOOM_MAX = 2.5,
  ZOOM_STEP = 0.25;
const PDF_RENDER_SCALE = 1.4;
const DRAW_CANVAS_W = 1560;
const DRAW_CANVAS_H = 570;
const DRAW_SCALE = DRAW_CANVAS_W / 520;
const NAME_CANVAS_W = 720;
const NAME_CANVAS_H = 176;
const VIEWER_GUTTER_X = 96;
const PV = '3.11.174';
const CDNS = [
  {
    main: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PV}/pdf.min.js`,
    worker: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PV}/pdf.worker.min.js`,
  },
  {
    main: `https://unpkg.com/pdfjs-dist@${PV}/build/pdf.min.js`,
    worker: `https://unpkg.com/pdfjs-dist@${PV}/build/pdf.worker.min.js`,
  },
];
let pdfReady = false;
function loadPdfJs(): Promise<void> {
  if (pdfReady && (window as any).pdfjsLib) return Promise.resolve();
  return new Promise((res, rej) => {
    let i = 0;
    const next = () => {
      if (i >= CDNS.length) {
        rej(new Error('pdf.js unavailable'));
        return;
      }
      const cdn = CDNS[i++];
      const s = document.createElement('script');
      s.src = cdn.main;
      s.onload = () => {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = cdn.worker;
        pdfReady = true;
        res();
      };
      s.onerror = () => {
        document.head.removeChild(s);
        next();
      };
      document.head.appendChild(s);
    };
    next();
  });
}

// ─── Types ───────────────────────────────────────────────────────────────────
type View = 'upload' | 'editor' | 'processing' | 'done';
type SS = 'idle' | 'active' | 'done' | 'error';
type ElementType = 'signature';
type Handle = 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'rotate';
type SignatureKind = 'draw' | 'upload' | 'text';

interface SigProfile {
  fullName: string;
  initials: string;
  signatureUrl: string;
  initialsUrl: string;
  signatureType?: SignatureKind;
  signatureAspect?: number;
  drawSignatureUrl?: string;
  uploadSignatureUrl?: string;
  textSignatureUrl?: string;
}

interface PlacedElement {
  id: string;
  type: ElementType;
  pageIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  signatureUrl?: string;
  signatureAspect?: number;
  content?: string;
}

interface DragState {
  handle: Handle;
  elId: string;
  px0: number;
  py0: number;
  x0: number;
  y0: number;
  w0: number;
  h0: number;
  rot0: number;
  pageIndex: number;
}

interface PageInfo {
  naturalW: number;
  naturalH: number;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  file: File;
  pageCount: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────
const PEN_COLORS = ['#111827', '#1E3A5F', '#374151', '#1F2D5A', '#14532D', '#7F1D1D'];
const NAME_FONTS = [
  { id: 'script', label: 'Script', css: "'Dancing Script', cursive" },
  { id: 'serif', label: 'Serif', css: "'Georgia', serif" },
  { id: 'sans', label: 'Modern', css: "'Inter', sans-serif" },
  { id: 'mono', label: 'Formal', css: "'Courier New', monospace" },
];
const ELEMENT_CONFIGS: Record<
  ElementType,
  { label: string; icon: string; color: string; defaultW: number; defaultH: number }
> = {
  signature: {
    label: 'Signature',
    icon: 'PencilSquareIcon',
    color: C.primary,
    defaultW: 200,
    defaultH: 80,
  },
};
const PSTEPS = [
  {
    id: 'upload',
    label: 'Uploading PDF',
    detail: 'Securely transferring your document',
    status: 'idle' as SS,
  },
  {
    id: 'sign',
    label: 'Applying Elements',
    detail: 'Embedding your signature elements',
    status: 'idle' as SS,
  },
  {
    id: 'finalize',
    label: 'Finalizing Document',
    detail: 'Preparing your signed PDF for download',
    status: 'idle' as SS,
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(b: number) {
  return b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
}
function StepIcon({ status }: { status: SS }) {
  const b = 'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0';
  if (status === 'done')
    return (
      <div className={b} style={{ background: C.primary }}>
        <Icon name="CheckIcon" size={14} className="text-white" />
      </div>
    );
  if (status === 'error')
    return (
      <div className={`${b} bg-red-500`}>
        <Icon name="XMarkIcon" size={14} className="text-white" />
      </div>
    );
  if (status === 'active')
    return (
      <div className={b} style={{ background: C.primary }}>
        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,.25)" strokeWidth="3" />
          <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
    );
  return (
    <div className={`${b} bg-gray-100`}>
      <div className="w-2 h-2 rounded-full bg-gray-400" />
    </div>
  );
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
// Trim transparent pixels from a canvas and return a tight data URL
function trimCanvasToDataUrl(src: HTMLCanvasElement): string {
  const ctx = src.getContext('2d')!;
  const { width, height } = src;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width,
    minY = height,
    maxX = 0,
    maxY = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const a = data[(y * width + x) * 4 + 3];
      if (a > 10) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX) return src.toDataURL();
  const pad = 6;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  out.getContext('2d')!.drawImage(src, minX, minY, cw, ch, 0, 0, cw, ch);
  return out.toDataURL('image/png');
}

// ─────────────────────────────────────────────────────────────────────────────
// SETUP MODAL
// ─────────────────────────────────────────────────────────────────────────────
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
async function getImageAspect(src: string): Promise<number> {
  const img = await loadImage(src);
  return clamp(img.width / Math.max(1, img.height), 0.3, 8);
}
async function trimImageToDataUrl(src: string): Promise<string> {
  const img = await loadImage(src);
  const cv = document.createElement('canvas');
  cv.width = img.width;
  cv.height = img.height;
  const ctx = cv.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const image = ctx.getImageData(0, 0, cv.width, cv.height);
  const data = image.data;
  let minX = cv.width,
    minY = cv.height,
    maxX = -1,
    maxY = -1;
  for (let y = 0; y < cv.height; y++) {
    for (let x = 0; x < cv.width; x++) {
      const i = (y * cv.width + x) * 4;
      const nearWhite = data[i] > 245 && data[i + 1] > 245 && data[i + 2] > 245;
      if (data[i + 3] > 10 && !nearWhite) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < minX) return src;
  const pad = 4;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(cv.width - 1, maxX + pad);
  maxY = Math.min(cv.height - 1, maxY + pad);
  const out = document.createElement('canvas');
  out.width = maxX - minX + 1;
  out.height = maxY - minY + 1;
  const outCtx = out.getContext('2d')!;
  outCtx.drawImage(cv, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
  const cropped = outCtx.getImageData(0, 0, out.width, out.height);
  for (let i = 0; i < cropped.data.length; i += 4) {
    if (cropped.data[i] > 245 && cropped.data[i + 1] > 245 && cropped.data[i + 2] > 245) {
      cropped.data[i + 3] = 0;
    }
  }
  outCtx.putImageData(cropped, 0, 0);
  return out.toDataURL('image/png');
}

function SetupModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: SigProfile | null;
  onSave: (p: SigProfile) => void;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const initTab: SignatureKind =
    initial?.signatureType || (initial?.signatureUrl ? 'upload' : 'draw');
  const initialDrawUrl =
    initial?.drawSignatureUrl || (initial?.signatureType === 'draw' ? initial.signatureUrl : null);
  const initialUploadUrl =
    initial?.uploadSignatureUrl ||
    (initial?.signatureType === 'upload' || (initial?.signatureUrl && !initial.signatureType)
      ? initial.signatureUrl
      : null);
  const initialTextUrl =
    initial?.textSignatureUrl || (initial?.signatureType === 'text' ? initial.signatureUrl : null);
  const [sigTab, setSigTab] = useState<SignatureKind>(initTab);
  const [penColor, setPenColor] = useState(PEN_COLORS[0]);
  const [strokeW, setStrokeW] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(Boolean(initialDrawUrl));
  const [drawSrc, setDrawSrc] = useState<string | null>(initialDrawUrl);
  const [imgSrc, setImgSrc] = useState<string | null>(initialUploadUrl);
  const [textSrc, setTextSrc] = useState<string | null>(initialTextUrl);
  const [nameText, setNameText] = useState(initial?.fullName || '');
  const [font, setFont] = useState('script');
  const [nameColor, setNameColor] = useState(PEN_COLORS[0]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasDrawnRef = useRef(Boolean(initialDrawUrl));

  useEffect(() => {
    if (sigTab !== 'draw' || !drawSrc || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext('2d')!;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, cv.width, cv.height);
      const scale = Math.min(cv.width / img.width, cv.height / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (cv.width - w) / 2, (cv.height - h) / 2, w, h);
      hasDrawnRef.current = true;
      setHasDrawn(true);
    };
    img.src = drawSrc;
  }, [drawSrc, sigTab]);

  const getPos = (e: any) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    const sx = cv.width / r.width,
      sy = cv.height / r.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * sx, y: (src.clientY - r.top) * sy };
  };
  const startDraw = (e: any) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = penColor;
    ctx.lineWidth = strokeW * DRAW_SCALE;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setIsDrawing(true);
  };
  const onDraw = (e: any) => {
    e.preventDefault();
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawnRef.current = true;
    setHasDrawn(true);
  };
  const endDraw = () => {
    setIsDrawing(false);
    if (hasDrawnRef.current && canvasRef.current)
      setDrawSrc(trimCanvasToDataUrl(canvasRef.current));
  };
  const clearDraw = () => {
    canvasRef.current!.getContext('2d')!.clearRect(0, 0, DRAW_CANVAS_W, DRAW_CANVAS_H);
    hasDrawnRef.current = false;
    setHasDrawn(false);
    setDrawSrc(null);
  };

  useEffect(() => {
    if (sigTab !== 'text') return;
    NAME_FONTS.forEach((f) => {
      const cv = document.getElementById(`nf-${f.id}`) as HTMLCanvasElement | null;
      if (!cv) return;
      const ctx = cv.getContext('2d')!;
      ctx.clearRect(0, 0, cv.width, cv.height);
      if (!nameText.trim()) return;
      ctx.fillStyle = nameColor;
      ctx.font = `${f.css.includes('cursive') ? 'italic ' : ''}88px ${f.css}`;
      ctx.textBaseline = 'middle';
      ctx.fillText(nameText.slice(0, 30), 32, cv.height / 2);
    });
  }, [nameText, nameColor, sigTab]);

  const handleSave = async () => {
    let signatureUrl = '';
    let nextDrawSrc = drawSrc || undefined;
    let nextUploadSrc = imgSrc || undefined;
    let nextTextSrc = textSrc || undefined;
    if (sigTab === 'draw') {
      if (!hasDrawn) {
        showToast('Please draw your signature.', 'error');
        return;
      }
      signatureUrl = trimCanvasToDataUrl(canvasRef.current!);
      nextDrawSrc = signatureUrl;
    } else if (sigTab === 'upload') {
      if (!imgSrc) {
        showToast('Please upload a signature image.', 'error');
        return;
      }
      signatureUrl = imgSrc;
      nextUploadSrc = signatureUrl;
    } else {
      if (!nameText.trim()) {
        showToast('Please type your name.', 'error');
        return;
      }
      const cv = document.getElementById(`nf-${font}`) as HTMLCanvasElement | null;
      if (!cv) return;
      signatureUrl = trimCanvasToDataUrl(cv);
      nextTextSrc = signatureUrl;
      setTextSrc(signatureUrl);
    }
    const signatureAspect = await getImageAspect(signatureUrl);
    onSave({
      fullName: nameText.trim() || 'Signature',
      initials: '',
      signatureUrl,
      initialsUrl: '',
      signatureType: sigTab,
      signatureAspect,
      drawSignatureUrl: nextDrawSrc,
      uploadSignatureUrl: nextUploadSrc,
      textSignatureUrl: nextTextSrc,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-heading font-extrabold text-[16px] text-brand-dark">
            Create Your Signature
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-400"
            aria-label="Close"
          >
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-5">
          {/* Tabs */}
          <div className="flex border-b border-gray-100 mb-4">
            {(['draw', 'upload', 'text'] as const).map((id) => (
              <button
                key={id}
                onClick={() => setSigTab(id)}
                className="flex-1 py-2.5 text-[13px] font-bold transition-all border-b-2 -mb-px capitalize"
                style={{
                  color: sigTab === id ? C.primary : '#9CA3AF',
                  borderColor: sigTab === id ? C.primary : 'transparent',
                }}
              >
                {id === 'draw'
                  ? 'Draw Signature'
                  : id === 'upload'
                    ? 'Upload Signature'
                    : 'Text Signature'}
              </button>
            ))}
          </div>

          {sigTab === 'draw' && (
            <div>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="flex gap-1.5">
                  {PEN_COLORS.map((col) => (
                    <button
                      key={col}
                      onClick={() => setPenColor(col)}
                      className="w-6 h-6 rounded-full border-2 hover:scale-110 transition-transform"
                      style={{
                        background: col,
                        borderColor: penColor === col ? C.primary : 'transparent',
                      }}
                    />
                  ))}
                </div>
                <input
                  type="range"
                  min={1}
                  max={8}
                  value={strokeW}
                  onChange={(e) => setStrokeW(+e.target.value)}
                  className="flex-1 accent-indigo-500 min-w-[80px]"
                />
                <button
                  onClick={clearDraw}
                  className="text-[12px] text-red-400 hover:text-red-600 font-semibold flex items-center gap-1"
                >
                  <Icon name="TrashIcon" size={12} /> Clear
                </button>
              </div>
              <canvas
                ref={canvasRef}
                width={DRAW_CANVAS_W}
                height={DRAW_CANVAS_H}
                onMouseDown={startDraw}
                onMouseMove={onDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={onDraw}
                onTouchEnd={endDraw}
                className="w-full rounded-xl border-2 border-dashed cursor-crosshair touch-none"
                style={{ borderColor: C.border, background: '#FAFAFE' }}
              />
            </div>
          )}

          {sigTab === 'upload' &&
            (imgSrc ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={imgSrc}
                  alt="Signature"
                  className="max-h-[160px] w-full object-contain rounded-xl border border-gray-100 bg-gray-50 p-3"
                />
                <label className="text-[13px] font-semibold text-gray-500 cursor-pointer hover:text-gray-800 flex items-center gap-1.5">
                  <Icon name="ArrowPathIcon" size={14} /> Replace
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      void fileToDataUrl(f)
                        .then(trimImageToDataUrl)
                        .then(setImgSrc)
                        .catch(() => showToast('Could not read signature image.', 'error'));
                    }}
                  />
                </label>
              </div>
            ) : (
              <label
                className="flex flex-col items-center justify-center py-12 rounded-xl border-2 border-dashed cursor-pointer hover:border-indigo-400 transition-colors"
                style={{ borderColor: C.border, background: C.surface }}
              >
                <Icon
                  name="PhotoIcon"
                  size={36}
                  variant="solid"
                  style={{ color: C.primary } as CSSProperties}
                />
                <span className="font-bold text-[14px] text-gray-800 mt-2">
                  Upload signature image
                </span>
                <span className="text-[12px] text-gray-400 mt-1">PNG or JPG</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    void fileToDataUrl(f)
                      .then(trimImageToDataUrl)
                      .then(setImgSrc)
                      .catch(() => showToast('Could not read signature image.', 'error'));
                  }}
                />
              </label>
            ))}

          {sigTab === 'text' && (
            <div>
              <input
                type="text"
                placeholder="Your name…"
                value={nameText}
                onChange={(e) => setNameText(e.target.value)}
                maxLength={40}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:outline-none text-[15px] mb-3 transition-all"
                style={{ borderColor: nameText ? C.border : undefined }}
              />
              <div className="grid grid-cols-2 gap-2 mb-3">
                {NAME_FONTS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFont(f.id)}
                    className="rounded-xl border-2 p-2 text-left transition-all"
                    style={{
                      borderColor: font === f.id ? C.primary : '#EEEEF5',
                      background: font === f.id ? C.surface : 'white',
                    }}
                  >
                    <span
                      className="block text-[10px] font-bold uppercase tracking-wider mb-1"
                      style={{ color: font === f.id ? C.primary : '#9CA3AF' }}
                    >
                      {f.label}
                    </span>
                    <canvas
                      id={`nf-${f.id}`}
                      width={NAME_CANVAS_W}
                      height={NAME_CANVAS_H}
                      className="w-full"
                    />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[12px] text-gray-400">Color:</span>
                {PEN_COLORS.map((col) => (
                  <button
                    key={col}
                    onClick={() => setNameColor(col)}
                    className="w-5 h-5 rounded-full border-2 hover:scale-110 transition-transform"
                    style={{
                      background: col,
                      borderColor: nameColor === col ? C.primary : 'transparent',
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleSave}
            className="mt-4 w-full py-3 rounded-xl font-extrabold text-white text-[13px] hover:-translate-y-px active:translate-y-0 transition-all"
            style={{ background: C.primary, boxShadow: C.shadow }}
          >
            Save &amp; Start Signing
          </button>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
// SIGNING PANEL
// ─────────────────────────────────────────────────────────────────────────────
function SigningPanel({
  profile,
  activeType,
  counts,
  onSelect,
  onSetup,
  totalPlaced,
  onApply,
  isApplyDisabled,
}: {
  profile: SigProfile | null;
  activeType: ElementType | null;
  counts: Record<ElementType, number>;
  onSelect: (t: ElementType) => void;
  onSetup: () => void;
  totalPlaced: number;
  onApply: () => void;
  isApplyDisabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
      <div className="flex items-center justify-between gap-2 px-1">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-wider">Sign Document</p>
          <p className="text-[13px] font-bold text-slate-900">
            {totalPlaced ? `${totalPlaced} placed` : 'Ready to sign'}
          </p>
        </div>
        <div
          className="h-9 w-9 rounded-xl flex items-center justify-center"
          style={{ background: C.surface }}
        >
          <Icon name="PencilSquareIcon" size={17} style={{ color: C.primary } as CSSProperties} />
        </div>
      </div>
      {/* Profile chip */}
      {profile ? (
        <div className="rounded-xl border p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-12 w-24 rounded-lg border p-1.5 shadow-sm dark:bg-white ">
              <img src={profile.signatureUrl} alt="sig" className="h-full w-full object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[12px] truncate">{profile.fullName}</p>
              <p className="text-[11px] ">Saved signature</p>
            </div>
            <button
              onClick={onSetup}
              className="h-8 w-8 rounded-lg flex items-center justify-center  dark:bg-gray-800 shadow-sm hover:shadow transition-all"
              aria-label="Edit signature"
            >
              <Icon name="PencilIcon" size={14} />
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={onSetup}
          className="flex items-center gap-3 px-3 py-3 rounded-xl border-2 border-dashed font-bold text-[13px] hover:-translate-y-px transition-all text-left"
          // style={{ borderColor: C.border, background: C.surface }}
        >
          <span className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm">
            <Icon name="UserCircleIcon" size={18} style={{ color: C.primary } as CSSProperties} />
          </span>
          <span className="min-w-0">
            <span className="block ">Create signature</span>
            <span className="block text-[11px] font-semibold ">Draw, upload, or type</span>
          </span>
        </button>
      )}

      <div className="h-px bg-slate-100" />

      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] font-extrabold uppercase tracking-wider">Elements</p>
        {activeType && (
          <span className="text-[10px] font-bold" style={{ color: C.primary }}>
            Placement active
          </span>
        )}
      </div>

      {/* Element type buttons */}
      {(
        Object.entries(ELEMENT_CONFIGS) as [ElementType, (typeof ELEMENT_CONFIGS)[ElementType]][]
      ).map(([type, cfg]) => {
        const isActive = activeType === type;
        const count = counts[type] || 0;
        return (
          <button
            key={type}
            onClick={() => onSelect(type)}
            className="group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all hover:-translate-y-px hover:shadow-md text-left w-full"
            style={{
              borderColor: isActive ? cfg.color : '',
              background: isActive ? `${cfg.color}10` : '',
              boxShadow: isActive ? `0 10px 24px ${cfg.color}18` : undefined,
            }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
              style={{ background: `${cfg.color}15` }}
            >
              <Icon
                name={cfg.icon as any}
                size={15}
                style={{ color: cfg.color } as CSSProperties}
              />
            </div>
            <span className="flex-1 min-w-0">
              <span className="block font-bold text-[13px] text-brand-dark">{cfg.label}</span>
              <span className="block text-[11px] font-medium text-brand-dark">
                {isActive ? 'Click the PDF to place' : 'Place on any page'}
              </span>
            </span>
            {count > 0 && (
              <span
                className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full text-white"
                style={{ background: cfg.color }}
              >
                {count}
              </span>
            )}
            {isActive && (
              <span className="text-[10px] font-bold animate-pulse" style={{ color: cfg.color }}>
                placing…
              </span>
            )}
          </button>
        );
      })}

      {/* Apply button */}
      <button
        onClick={onApply}
        disabled={isApplyDisabled}
        className="mt-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-extrabold text-[13px] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        style={{ background: C.primary, boxShadow: C.shadow }}
      >
        <span className="text-white flex gap-1 items-center">
          <Icon name="CheckBadgeIcon" size={15} variant="solid" />
          Apply & Download
        </span>
        {totalPlaced > 0 && (
          <span
            className="ml-1 px-1.5 py-0.5 rounded-full bg-white text-[10px] font-extrabold"
            // style={{ color: C.primary }}
          >
            {totalPlaced}
          </span>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLACED ELEMENT NODE
// ─────────────────────────────────────────────────────────────────────────────
const HANDLES: { id: Handle; cursor: string; pos: CSSProperties }[] = [
  { id: 'nw', cursor: 'nw-resize', pos: { top: -5, left: -5 } },
  { id: 'n', cursor: 'n-resize', pos: { top: -5, left: '50%', transform: 'translateX(-50%)' } },
  { id: 'ne', cursor: 'ne-resize', pos: { top: -5, right: -5 } },
  { id: 'e', cursor: 'e-resize', pos: { top: '50%', right: -5, transform: 'translateY(-50%)' } },
  { id: 'se', cursor: 'se-resize', pos: { bottom: -5, right: -5 } },
  { id: 's', cursor: 's-resize', pos: { bottom: -5, left: '50%', transform: 'translateX(-50%)' } },
  { id: 'sw', cursor: 'sw-resize', pos: { bottom: -5, left: -5 } },
  { id: 'w', cursor: 'w-resize', pos: { top: '50%', left: -5, transform: 'translateY(-50%)' } },
];

function PlacedElementNode({
  el,
  signatureUrl,
  isSelected,
  zoom,
  onPointerDown,
  onDelete,
}: {
  el: PlacedElement;
  signatureUrl?: string;
  isSelected: boolean;
  zoom: number;
  onPointerDown: (e: React.PointerEvent, handle: Handle) => void;
  onDelete: () => void;
}) {
  const cfg = ELEMENT_CONFIGS[el.type];

  const renderContent = () => {
    if (el.type === 'signature') {
      return signatureUrl ? (
        <img
          src={signatureUrl}
          alt="Signature"
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            pointerEvents: 'none',
            display: 'block',
            imageRendering: 'auto',
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[11px] text-gray-400">
          No signature
        </div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: el.x * zoom,
        top: el.y * zoom,
        width: el.w * zoom,
        height: el.h * zoom,
        transform: `rotate(${el.rotation}deg)`,
        transformOrigin: 'center center',
        cursor: 'move',
        userSelect: 'none',
        outline: isSelected ? `2px solid ${cfg.color}` : `1.5px dashed ${cfg.color}55`,
        outlineOffset: 2,
        borderRadius: 2,
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        onPointerDown(e, 'move');
      }}
    >
      {renderContent()}

      {isSelected && (
        <>
          {/* Rotation stem */}
          <div
            style={{
              position: 'absolute',
              top: -26,
              left: '50%',
              width: 1,
              height: 22,
              background: cfg.color,
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
            }}
          />
          {/* Rotation handle */}
          <div
            style={{
              position: 'absolute',
              top: -38,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: cfg.color,
              border: '2px solid white',
              boxShadow: '0 2px 6px rgba(0,0,0,.2)',
              cursor: 'grab',
              zIndex: 11,
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDown(e, 'rotate');
            }}
          />
          {/* 8 resize handles */}
          {HANDLES.map((h) => (
            <div
              key={h.id}
              style={{
                position: 'absolute',
                width: 10,
                height: 10,
                background: 'white',
                border: `2px solid ${cfg.color}`,
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                zIndex: 10,
                cursor: h.cursor,
                ...h.pos,
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                onPointerDown(e, h.id);
              }}
            />
          ))}
          {/* Delete button */}
          <button
            style={{
              position: 'absolute',
              top: -30,
              right: -6,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#EF4444',
              border: '2px solid white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 12,
              boxShadow: '0 1px 4px rgba(0,0,0,.2)',
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Remove"
          >
            <Icon name="XMarkIcon" size={10} className="text-white" />
          </button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function SignPDFUploader() {
  const { showToast } = useToast();
  const [view, setView] = useState<View>('upload');
  const [file, setFile] = useState<UploadedFile | null>(null);
  const [profile, setProfile] = useState<SigProfile | null>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [pendingType, setPendingType] = useState<ElementType | null>(null);
  const [activeType, setActiveType] = useState<ElementType | null>(null);
  const [elements, setElements] = useState<PlacedElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1.0);
  const [pageInfos, setPageInfos] = useState<PageInfo[]>([]);
  const [fitScale, setFitScale] = useState(0);
  const [steps, setSteps] = useState(PSTEPS.map((s) => ({ ...s })));
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const pdfDocRef = useRef<any>(null);
  const pdfObjUrl = useRef<string | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const overlayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const renderTasksRef = useRef<any[]>([]);
  const renderSeqRef = useRef(0);
  const dragRef = useRef<DragState | null>(null);
  // Tracks whether a pointer-down landed on a placed element (to suppress overlay deselect)
  const elementHitRef = useRef(false);
  const progTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (progTimer.current) clearInterval(progTimer.current);
    },
    []
  );

  const animateTo = (t: number) => {
    if (progTimer.current) clearInterval(progTimer.current);
    progTimer.current = setInterval(
      () =>
        setProgress((p) => {
          if (p >= t) {
            clearInterval(progTimer.current!);
            return t;
          }
          return Math.min(p + 1, t);
        }),
      20
    );
  };
  const setStatus = (id: string, s: SS) =>
    setSteps((prev) => prev.map((x) => (x.id === id ? { ...x, status: s } : x)));

  useLayoutEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || pageInfos.length === 0) return;

    const updateFitScale = () => {
      const maxPageW = Math.max(...pageInfos.map((info) => info.naturalW));
      const availableW = Math.max(280, viewer.clientWidth - VIEWER_GUTTER_X);
      setFitScale(clamp(availableW / (maxPageW * PDF_RENDER_SCALE), 0.35, 1));
    };

    updateFitScale();
    const observer = new ResizeObserver(updateFitScale);
    observer.observe(viewer);
    return () => observer.disconnect();
  }, [pageInfos]);

  // ── PDF Load ───────────────────────────────────────────────────────────────
  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const f = files[0];
      if (!f.name.endsWith('.pdf') && f.type !== 'application/pdf') {
        showToast('Please upload a PDF file.', 'error');
        return;
      }
      if (f.size > MAX_MB) {
        showToast('File too large. Max 50 MB.', 'error');
        return;
      }
      const enc = await isEncryptedPDF(f);
      if (enc) {
        showToast('PDF is password-protected. Unlock it first.', 'error', {
          text: 'Unlock PDF',
          href: '/unlock-pdf',
        });
        return;
      }
      const meta = await getPdfMeta(f);
      setFile(meta);
      setElements([]);
      setSelectedId(null);
      setActiveType(null);
      setFitScale(0);
      setView('editor');
      try {
        await loadPdfJs();
        if (pdfObjUrl.current) URL.revokeObjectURL(pdfObjUrl.current);
        const url = URL.createObjectURL(f);
        pdfObjUrl.current = url;
        const doc = await (window as any).pdfjsLib.getDocument(url).promise;
        pdfDocRef.current = doc;
        const n = doc.numPages;
        canvasRefs.current = new Array(n).fill(null);
        overlayRefs.current = new Array(n).fill(null);
        renderTasksRef.current.forEach((task) => task?.cancel?.());
        renderTasksRef.current = [];
        const infos: PageInfo[] = [];
        for (let i = 1; i <= n; i++) {
          const pg = await doc.getPage(i);
          const vp = pg.getViewport({ scale: PDF_RENDER_SCALE });
          infos.push({
            naturalW: vp.width / PDF_RENDER_SCALE,
            naturalH: vp.height / PDF_RENDER_SCALE,
          });
        }
        setPageInfos(infos);
      } catch (e) {
        console.error(e);
        showToast('Could not load PDF.', 'error');
      }
    },
    [showToast]
  );

  // Render each page canvas when zoom or pageInfos change
  useEffect(() => {
    if (!pdfDocRef.current || pageInfos.length === 0 || fitScale <= 0) return;
    const doc = pdfDocRef.current;
    const seq = ++renderSeqRef.current;
    const previousTasks = renderTasksRef.current;
    renderTasksRef.current = [];
    previousTasks.forEach((task) => task?.cancel?.());

    pageInfos.forEach((_, i) => {
      void (async () => {
        await Promise.allSettled(previousTasks.map((task) => task?.promise).filter(Boolean));
        if (seq !== renderSeqRef.current) return;
        const cv = canvasRefs.current[i];
        if (!cv) return;
        let task: any = null;
        try {
          const pg = await doc.getPage(i + 1);
          if (seq !== renderSeqRef.current) return;
          const vp = pg.getViewport({ scale: PDF_RENDER_SCALE * zoom * fitScale });
          cv.width = vp.width;
          cv.height = vp.height;
          const ctx = cv.getContext('2d')!;
          ctx.clearRect(0, 0, vp.width, vp.height);
          task = pg.render({ canvasContext: ctx, viewport: vp });
          renderTasksRef.current[i] = task;
          await task.promise;
        } catch (e: any) {
          if (e?.name !== 'RenderingCancelledException') console.error(e);
        } finally {
          if (renderTasksRef.current[i] === task) renderTasksRef.current[i] = null;
        }
      })();
    });
  }, [zoom, fitScale, pageInfos]);

  useEffect(
    () => () => {
      renderSeqRef.current += 1;
      renderTasksRef.current.forEach((task) => task?.cancel?.());
      renderTasksRef.current = [];
    },
    []
  );

  // ── Placement ──────────────────────────────────────────────────────────────
  const handleSelectType = (type: ElementType) => {
    if (!profile) {
      setPendingType(type);
      setShowSetup(true);
      return;
    }
    setActiveType((prev) => (prev === type ? null : type));
    setSelectedId(null);
  };

  const handlePageClick = useCallback(
    (e: React.MouseEvent, pageIndex: number) => {
      // If pointer-down landed on a placed element, don't deselect
      if (elementHitRef.current) {
        elementHitRef.current = false;
        return;
      }
      if (dragRef.current) return;
      if (!activeType) {
        setSelectedId(null);
        return;
      }
      const overlay = overlayRefs.current[pageIndex];
      if (!overlay) return;
      const rect = overlay.getBoundingClientRect();
      const info = pageInfos[pageIndex];
      if (!info) return;
      const pageScale = zoom * PDF_RENDER_SCALE * fitScale;
      const xPx = (e.clientX - rect.left) / pageScale;
      const yPx = (e.clientY - rect.top) / pageScale;
      const cfg = ELEMENT_CONFIGS[activeType];
      const aspect =
        activeType === 'signature'
          ? profile?.signatureAspect || cfg.defaultW / cfg.defaultH
          : cfg.defaultW / cfg.defaultH;
      const defaultW = cfg.defaultW;
      const defaultH = clamp(defaultW / aspect, 24, 120);
      const el: PlacedElement = {
        id: crypto.randomUUID(),
        type: activeType,
        pageIndex,
        x: Math.max(0, Math.min(info.naturalW - defaultW, xPx - defaultW / 2)),
        y: Math.max(0, Math.min(info.naturalH - defaultH, yPx - defaultH / 2)),
        w: defaultW,
        h: defaultH,
        rotation: 0,
        signatureUrl: activeType === 'signature' ? profile?.signatureUrl : undefined,
        signatureAspect: activeType === 'signature' ? aspect : undefined,
        content: '',
      };
      setElements((prev) => [...prev, el]);
      setSelectedId(el.id);
      setActiveType(null);
    },
    [activeType, zoom, fitScale, pageInfos, profile]
  );

  // ── Drag/Resize ────────────────────────────────────────────────────────────
  const handleElementPointerDown = useCallback(
    (e: React.PointerEvent, elId: string, handle: Handle, pageIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      // Signal to overlay click handler that this click came from an element
      elementHitRef.current = true;
      const el = elements.find((x) => x.id === elId);
      if (!el) return;
      setSelectedId(elId);
      setActiveType(null);
      dragRef.current = {
        handle,
        elId,
        px0: e.clientX,
        py0: e.clientY,
        x0: el.x,
        y0: el.y,
        w0: el.w,
        h0: el.h,
        rot0: el.rotation,
        pageIndex,
      };
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture can fail if the browser has already ended this pointer.
      }
    },
    [elements]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent, pageIndex: number) => {
      const ds = dragRef.current;
      if (!ds || ds.pageIndex !== pageIndex) return;
      const info = pageInfos[pageIndex];
      if (!info) return;
      const pageScale = zoom * PDF_RENDER_SCALE * fitScale;
      const dx = (e.clientX - ds.px0) / pageScale;
      const dy = (e.clientY - ds.py0) / pageScale;
      const { handle: hdl, x0, y0, w0, h0, rot0 } = ds;
      setElements((prev) =>
        prev.map((el) => {
          if (el.id !== ds.elId) return el;
          let { x, y, w, rotation } = el;
          let elH = el.h;
          if (hdl === 'move') {
            x = clamp(x0 + dx, 0, Math.max(0, info.naturalW - w0));
            y = clamp(y0 + dy, 0, Math.max(0, info.naturalH - h0));
          } else if (hdl === 'rotate') {
            const cx = x0 + w0 / 2,
              cy = y0 + h0 / 2;
            const overlay = overlayRefs.current[pageIndex];
            if (overlay) {
              const rect = overlay.getBoundingClientRect();
              const a0 = Math.atan2(
                ds.py0 - rect.top - cy * pageScale,
                ds.px0 - rect.left - cx * pageScale
              );
              const a1 = Math.atan2(
                e.clientY - rect.top - cy * pageScale,
                e.clientX - rect.left - cx * pageScale
              );
              rotation = rot0 + (a1 - a0) * (180 / Math.PI);
            }
          } else {
            let nx = x0,
              ny = y0,
              nw = w0,
              nh = h0;
            if (hdl.includes('e')) nw = Math.max(30, w0 + dx);
            if (hdl.includes('s')) nh = Math.max(20, h0 + dy);
            if (hdl.includes('w')) {
              nw = Math.max(30, w0 - dx);
              nx = x0 + w0 - nw;
            }
            if (hdl.includes('n')) {
              nh = Math.max(20, h0 - dy);
              ny = y0 + h0 - nh;
            }
            if (nx < 0) {
              nw += nx;
              nx = 0;
            }
            if (ny < 0) {
              nh += ny;
              ny = 0;
            }
            nw = clamp(nw, 30, Math.max(30, info.naturalW - nx));
            nh = clamp(nh, 20, Math.max(20, info.naturalH - ny));
            x = clamp(nx, 0, Math.max(0, info.naturalW - nw));
            y = clamp(ny, 0, Math.max(0, info.naturalH - nh));
            w = nw;
            elH = nh;
          }
          return { ...el, x, y, w, h: elH, rotation };
        })
      );
    },
    [zoom, fitScale, pageInfos]
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  useEffect(() => {
    const clearDrag = () => {
      dragRef.current = null;
      window.setTimeout(() => {
        elementHitRef.current = false;
      }, 0);
    };
    window.addEventListener('pointerup', clearDrag);
    window.addEventListener('pointercancel', clearDrag);
    return () => {
      window.removeEventListener('pointerup', clearDrag);
      window.removeEventListener('pointercancel', clearDrag);
    };
  }, []);

  const removeElement = (id: string) => {
    setElements((prev) => prev.filter((e) => e.id !== id));
    setSelectedId(null);
  };
  const editContent = (id: string, val: string) =>
    setElements((prev) => prev.map((e) => (e.id === id ? { ...e, content: val } : e)));

  // ── Submit ─────────────────────────────────────────────────────────────────
  const totalPlaced = elements.length;
  const countsByType = elements.reduce(
    (acc, el) => {
      acc[el.type] = (acc[el.type] || 0) + 1;
      return acc;
    },
    {} as Record<ElementType, number>
  );

  const handleApply = async () => {
    if (!totalPlaced) {
      showToast('Place at least one element on the PDF.', 'error');
      return;
    }
    if (!profile) {
      showToast('Complete signature setup first.', 'error');
      return;
    }
    const hasSig = elements.some((e) => e.type === 'signature');
    if (!hasSig) {
      showToast('A Signature element is required.', 'error');
      return;
    }
    setView('processing');
    setSteps(PSTEPS.map((s) => ({ ...s, status: 'idle' })));
    setProgress(0);
    try {
      setStatus('upload', 'active');
      animateTo(25);

      // Group elements by their signature image URL.
      // Each unique image produces one entry with all its placements collected under it.
      // Positions use bottom-left origin (PDF coordinate system):
      //   pdfY = pageHeight - el.y - el.height  (el.y is top-left, y-down)
      const sigMap = new Map<string, { imageData: string; positions: object[] }>();

      for (const el of elements) {
        const imgUrl = el.signatureUrl ?? profile!.signatureUrl;
        const info = pageInfos[el.pageIndex];
        const position = {
          pageIndex: el.pageIndex, // 0-based
          x: el.x, // already in PDF points
          y: info.naturalH - el.y - el.h,
          width: el.w,
          height: el.h,
        };
        if (!sigMap.has(imgUrl)) {
          sigMap.set(imgUrl, { imageData: imgUrl, positions: [] });
        }
        sigMap.get(imgUrl)!.positions.push(position);
      }

      // Final payload: { signatures: [{ imageData, positions[] }, ...] }
      const signatures = Array.from(sigMap.values());

      const formData = new FormData();
      formData.append('file', file!.file);
      formData.append('signatures', JSON.stringify(signatures));

      setStatus('upload', 'done');
      setStatus('sign', 'active');
      animateTo(65);

      const res = await fetch(`${API_BASE}/api/pdf/sign-pdf`, {
        method: 'PUT',
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Signing failed');

      setStatus('sign', 'done');
      setStatus('finalize', 'active');
      animateTo(95);

      setDownloadUrl(data.pdfUrl);
      await new Promise((r) => setTimeout(r, 500));
      setStatus('finalize', 'done');
      animateTo(100);
      setView('done');
    } catch (err: any) {
      showToast(err.message || 'An error occurred.', 'error');
      setSteps((prev) => prev.map((s) => (s.status === 'active' ? { ...s, status: 'error' } : s)));
      setView('editor');
    }
  };

  const reset = () => {
    setView('upload');
    setFile(null);
    setElements([]);
    setSelectedId(null);
    setActiveType(null);
    setPageInfos([]);
    setZoom(1);
    setFitScale(0);
    setDownloadUrl(null);
    setSteps(PSTEPS.map((s) => ({ ...s, status: 'idle' })));
    setProgress(0);
    pdfDocRef.current = null;
    if (pdfObjUrl.current) {
      URL.revokeObjectURL(pdfObjUrl.current);
      pdfObjUrl.current = null;
    }
  };

  // ── Upload View ────────────────────────────────────────────────────────────
  if (view === 'upload')
    return (
      <div className="w-full max-w-6xl mx-auto px-4">
        <UploadZone
          onFilesSelected={handleFiles}
          multiple={false}
          accentColor={C.primary}
          iconName="PencilSquareIcon"
          title="Drop PDF here to sign"
          subtitle="or click to browse — single file only"
          buttonText="Select PDF File"
          dragTitle="Drop your PDF here!"
        />
      </div>
    );

  // ── Processing View ────────────────────────────────────────────────────────
  if (view === 'processing')
    return (
      <div className="w-full max-w-xl mx-auto animate-in fade-in duration-300">
        <div className="flex justify-between mb-2">
          <span className="font-heading font-semibold text-[13px] text-brand-mid">
            Signing your PDF…
          </span>
          <span className="font-heading font-extrabold text-[13px]" style={{ color: C.primary }}>
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-slate-100 mb-5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${progress}%`,
              background: `linear-gradient(90deg,${C.primary},#818CF8)`,
            }}
          />
        </div>
        <div className="space-y-3">
          {steps.map((step) => (
            <div
              key={step.id}
              className="flex items-start gap-3 p-3 rounded-xl transition-all"
              style={{
                background:
                  step.status === 'active'
                    ? C.surface
                    : step.status === 'done'
                      ? '#F0FDF4'
                      : step.status === 'error'
                        ? '#FEF2F2'
                        : '#F8F8FC',
                border: `1.5px solid ${step.status === 'active' ? C.border : step.status === 'done' ? '#BBF7D0' : step.status === 'error' ? '#FECACA' : '#EEEEF5'}`,
              }}
            >
              <StepIcon status={step.status} />
              <div className="flex-1">
                <p
                  className="font-heading font-bold text-[13px]"
                  style={{
                    color:
                      step.status === 'done'
                        ? '#10B981'
                        : step.status === 'error'
                          ? '#DC2626'
                          : step.status === 'active'
                            ? '#1A1A2E'
                            : '#8888A8',
                  }}
                >
                  {step.label}
                </p>
                {(step.status === 'active' || step.status === 'done') && (
                  <p className="text-[12px] text-brand-muted mt-0.5">{step.detail}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );

  const downloadFilename = file ? file.name.replace(/\.pdf$/i, '-signed.pdf') : 'signed.pdf';

  const handleDownload = async () => {
    if (!downloadUrl) return;

    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  // ── Done View ──────────────────────────────────────────────────────────────
  if (view === 'done')
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 pulse-anim bg-green-50">
          <Icon name="CheckBadgeIcon" size={42} variant="solid" className="text-green-600" />
        </div>
        <h3 className="font-heading font-bold mb-2 text-[20px] text-brand-dark">
          PDF Signed Successfully!
        </h3>
        <p className="text-brand-mid text-[14px] font-body mb-6">
          Your signatures have been successfully applied to the document.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <a
            onClick={handleDownload}
            className="sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary text-white border-none font-heading font-bold text-[15px] cursor-pointer shadow-[0_6px_20px_rgba(232,68,90,0.28)]"
            style={{
              backgroundColor: SIGN_THEME.primary,
              boxShadow: '0 6px 20px ' + SIGN_THEME.primaryShadow,
            }}
          >
            <Icon name="ArrowDownTrayIcon" size={18} variant="solid" /> Download Signed PDF
          </a>
          <button
            onClick={reset}
            className="px-8 py-4 rounded-full border-2 border-brand-border font-heading font-bold text-brand-mid hover:bg-brand-surface transition-all active:scale-95 flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2"
          >
            <Icon name="ArrowPathIcon" size={18} /> Sign Another PDF
          </button>
        </div>
      </div>
    );

  // ── Editor View ────────────────────────────────────────────────────────────
  const zoomInOutClass =
    'w-7 h-7 rounded-lg flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-black dark:hover:text-white disabled:opacity-40 transition-colors';
  return (
    <>
      {/* Setup Modal */}
      {showSetup && (
        <SetupModal
          initial={profile}
          onSave={(p) => {
            setProfile(p);
            setShowSetup(false);
            if (pendingType) {
              setActiveType(pendingType);
              setPendingType(null);
            }
            showToast('Signature setup complete! Click on the PDF to place elements.', 'success');
          }}
          onClose={() => {
            setShowSetup(false);
            setPendingType(null);
          }}
        />
      )}

      <div
        className="w-full flex flex-col lg:flex-row gap-4"
        onKeyDown={(e) => {
          if (e.key === 'Escape') setActiveType(null);
          if (
            (e.key === 'Delete' || e.key === 'Backspace') &&
            selectedId &&
            !(e.target as HTMLElement).closest('input')
          )
            removeElement(selectedId);
        }}
        tabIndex={-1}
      >
        {/* ── Left: Toolbar + PDF Viewer ───────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          {/* Toolbar */}
          {file && <FileCard file={file} onRemove={reset} theme={SIGN_THEME} />}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Zoom */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-xl border border-gray-200 ">
              <button
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                disabled={zoom <= ZOOM_MIN}
                className={zoomInOutClass}
                aria-label="Zoom out"
              >
                <Icon name="MinusIcon" size={14} />
              </button>
              <span className="text-[13px] font-bold w-10 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                disabled={zoom >= ZOOM_MAX}
                className={zoomInOutClass}
                aria-label="Zoom in"
              >
                <Icon name="PlusIcon" size={14} />
              </button>
            </div>
            <div className="flex-1" />
            {/* Active mode banner */}
            {activeType && (
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-[12px] font-semibold animate-in slide-in-from-top-1 duration-200"
                style={{
                  background: `${ELEMENT_CONFIGS[activeType].color}12`,
                  border: `1.5px dashed ${ELEMENT_CONFIGS[activeType].color}`,
                  color: ELEMENT_CONFIGS[activeType].color,
                }}
              >
                <Icon
                  name="CursorArrowRaysIcon"
                  size={14}
                  style={{ color: ELEMENT_CONFIGS[activeType].color } as CSSProperties}
                />
                Click on the PDF to place {ELEMENT_CONFIGS[activeType].label}
                <button
                  onClick={() => setActiveType(null)}
                  className="ml-1 opacity-60 hover:opacity-100"
                >
                  ✕
                </button>
              </div>
            )}
            {/* Selected info */}
            {selectedId && !activeType && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-[12px] border border-gray-200 bg-white">
                <span className="text-gray-400">Drag · Resize · Rotate · Delete</span>
                <button
                  onClick={() => removeElement(selectedId)}
                  className="ml-1 text-red-400 hover:text-red-600 font-semibold flex items-center gap-1"
                >
                  <Icon name="TrashIcon" size={12} /> Delete
                </button>
              </div>
            )}
          </div>

          {/* PDF Scroll Container — all pages */}
          <div
            ref={viewerRef}
            className="relative rounded-2xl border border-gray-200 bg-gray-100 overflow-auto"
            style={{ maxHeight: 'min(72vh, 740px)', minHeight: 300 }}
          >
            <div className="flex flex-col items-center gap-12 py-12 px-12">
              {(pageInfos.length === 0 || fitScale <= 0) && (
                <div className="flex flex-col items-center justify-center gap-3 py-20">
                  <svg className="animate-spin w-10 h-10" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#E0E0F0" strokeWidth="3" />
                    <path
                      d="M12 2a10 10 0 0 1 10 10"
                      stroke={C.primary}
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="text-[14px] text-gray-400 font-medium">Loading PDF…</span>
                </div>
              )}

              {fitScale > 0 &&
                pageInfos.map((info, pageIndex) => {
                  const pageElements = elements.filter((e) => e.pageIndex === pageIndex);
                  const pageScale = zoom * PDF_RENDER_SCALE * fitScale;
                  const displayW = info.naturalW * pageScale;
                  const displayH = info.naturalH * pageScale;
                  return (
                    <div
                      key={pageIndex}
                      className="relative flex-shrink-0 shadow-lg rounded"
                      style={{ width: displayW, height: displayH }}
                    >
                      {/* Page label */}
                      <div className="absolute -top-5 left-0 text-[11px] font-bold text-gray-400">
                        Page {pageIndex + 1}
                      </div>
                      {/* Canvas */}
                      <canvas
                        ref={(el) => {
                          canvasRefs.current[pageIndex] = el;
                        }}
                        className="block w-full rounded"
                        style={{ cursor: activeType ? 'crosshair' : 'default' }}
                      />
                      {/* Overlay */}
                      <div
                        ref={(el) => {
                          overlayRefs.current[pageIndex] = el;
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          cursor: activeType ? 'crosshair' : 'default',
                        }}
                        onClick={(e) => handlePageClick(e, pageIndex)}
                        onPointerMove={(e) => handlePointerMove(e, pageIndex)}
                        onPointerUp={handlePointerUp}
                      >
                        {/* Alignment guide lines */}
                        {dragRef.current?.pageIndex === pageIndex &&
                          (() => {
                            const ds = dragRef.current!;
                            const el = elements.find((e) => e.id === ds.elId);
                            if (!el) return null;
                            const cx = (el.x + el.w / 2) * pageScale;
                            const cy = (el.y + el.h / 2) * pageScale;
                            const midX = (info.naturalW / 2) * pageScale;
                            const midY = (info.naturalH / 2) * pageScale;
                            const snapX = Math.abs(cx - midX) < 8;
                            const snapY = Math.abs(cy - midY) < 8;
                            return (
                              <svg
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  pointerEvents: 'none',
                                  width: '100%',
                                  height: '100%',
                                }}
                              >
                                {snapX && (
                                  <line
                                    x1={midX}
                                    y1="0"
                                    x2={midX}
                                    y2="100%"
                                    stroke={C.primary}
                                    strokeWidth="1"
                                    strokeDasharray="4 3"
                                    opacity="0.7"
                                  />
                                )}
                                {snapY && (
                                  <line
                                    x1="0"
                                    y1={midY}
                                    x2="100%"
                                    y2={midY}
                                    stroke={C.primary}
                                    strokeWidth="1"
                                    strokeDasharray="4 3"
                                    opacity="0.7"
                                  />
                                )}
                              </svg>
                            );
                          })()}

                        {/* Placed elements */}
                        {pageElements.map((el) => (
                          <PlacedElementNode
                            key={el.id}
                            el={el}
                            signatureUrl={el.signatureUrl || profile?.signatureUrl}
                            isSelected={selectedId === el.id}
                            zoom={pageScale}
                            onPointerDown={(e, handle) =>
                              handleElementPointerDown(e, el.id, handle, pageIndex)
                            }
                            onDelete={() => removeElement(el.id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Summary */}
          {totalPlaced > 0 && (
            <p className="text-center text-[12px] text-gray-400">
              {totalPlaced} element{totalPlaced > 1 ? 's' : ''} placed across{' '}
              {new Set(elements.map((e) => e.pageIndex)).size} page
              {new Set(elements.map((e) => e.pageIndex)).size > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* ── Right: Signing Panel ──────────────────────────────────────────── */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <div className="sticky top-4">
            <SigningPanel
              profile={profile}
              activeType={activeType}
              counts={countsByType as Record<ElementType, number>}
              onSelect={handleSelectType}
              onSetup={() => setShowSetup(true)}
              totalPlaced={totalPlaced}
              onApply={handleApply}
              isApplyDisabled={totalPlaced === 0}
            />
          </div>
        </div>
      </div>
    </>
  );
}
